import axios from 'axios';

const defaultState = {
  loaded: false,
  candidateCount: null,
  employerCount: null,
  industries: [],
  industryCounts: {},
  popularLocations: [],
  userCountry: ''
};

const getters = {
  /**
   * Returns a list of popular agency objects.
   * @returns [{name: string, url: string}]
   */
  getPopularLocationLinks(state) {
    return state.popularLocations
      .filter((location) => {
        return !(
          typeof location !== 'object' ||
          location.Label === undefined ||
          location.UrlLocator === undefined
        );
      })
      .map((location) => {
        const labelSwapMap = {
          'Greater London': 'London',
          'Newcastle Upon Tyne': 'Newcastle',
        };
        let name = location.Label;
        if (Object.keys(labelSwapMap).indexOf(name) !== -1) {
          name = labelSwapMap[name];
        }
        const url = `/agencysearch/by${location.LocationType}/${location.UrlLocator}.htm`;

        return { name, url };
      });
  },
  /**
   * Returns a list of popular industry as link objects.
   * @returns [{name: string, url: string}]
   */
  getPopularIndustryLinks(state, getters) {
    const popularIndustries = [
      'Administration',
      'Construction',
      'IT',
      'Health Care',
      'Engineering',
      'Catering and Hospitality',
      'Driving',
      'Education',
      'Retail',
      'Industrial',
      'Manufacturing / Production',
      'Accounting & Finance',
      'Social Care',
      'Call Centre / Customer Service',
      'Food & Drink',
      'Media',
      'Sales',
      'Creative / Design',
    ];

    return getters.getAllIndustryLinks.filter((industryLink) => {
      return popularIndustries.includes(industryLink.name);
    });
  },
  /**
   * Returns a list of all industries as link objects.
   * @returns [{name: string, url: string}]
   */
  getAllIndustryLinks(state) {
    return state.industries
      .filter((industry) => {
        const excludedIndustryNames = ['domestic', 'miscellaneous'];
        return !excludedIndustryNames.includes(industry.AC_industry_id);
      })
      .map((industry) => {
        const name = `${industry.industry_name_1}`; // Capitalised industry name
        const url = `/agencysearch/${industry.AC_industry_id}/agencysearch.htm`;
        const industry_id = `${industry.AC_industry_id}`;
        return { name, url, industry_id };
      })
      .sort((industryA, industryB) => {
        if (industryA.name > industryB.name) {
          return 1;
        }
        if (industryA.name < industryB.name) {
          return -1;
        }
        return 0;
      });
  },
  /**
   * Returns a list of the top 15 industries for the industry select modal
   */
  getTop15Industries(state) {
    return state.industries.filter((industry) => {
      const top15 = [
        'accounting',
        'admin',
        'agriculture',
        'catering',
        'construction',
        'driving',
        'education',
        'engineering',
        'health',
        'industrial',
        'IT',
        'manufacture',
        'retail',
        'security',
        'socialcare',
      ];
      return top15.includes(industry.AC_industry_id);
    });
  },
  /**
   * Returns a list of industries in a category
   */
  getCategory: (state) => (category) => {
    const categoryIndustryMap = {
      technical: [
        'agriculture',
        'biotechnology',
        'construction',
        'defence',
        'digital',
        'electronics',
        'energy',
        'engineering',
        'environ',
        'facilitiesmanagement',
        'food',
        'government',
        'healthsafety',
        'housebuilding',
        'industrial',
        'manufacture',
        'pharm',
        'property',
        'science',
        'security',
        'telecoms',
      ],
      social: [
        'childcare',
        'education',
        'health',
        'socialcare',
        'trainingandlearning',
      ],
      office: [
        'admin',
        'custservcallcentre',
        'hr',
        'informationindustry',
        'IT',
        'legal',
        'marketresearch',
        'marketing',
        'multilingual',
        'pr',
        'projectmanagement',
        'printing',
        'purchasing',
        'sales',
        'telecoms',
      ],
      financial: [
        'accounting',
        'banking',
        'financialservice',
        'insurance',
        'tax',
      ],
      misc: ['graduate', 'recruitment'],
      voluntary: ['charity', 'notforprofit'],
      fashion: ['fashion', 'fmcg', 'retail'],
      executive: ['consulting', 'headhunting', 'executive'],
      media: [
        'arts',
        'catering',
        'creative',
        'mediapr',
        'spabeauty',
        'sport',
        'travel',
      ],
      transport: [
        'automotive',
        'aviation',
        'driving',
        'logistics',
        'rail',
        'shipping',
        'spaceandsatellite',
      ],
    };
    if (!Object.keys(categoryIndustryMap).includes(category)) {
      return [];
    }

    return Object.values(state.industryCounts).filter((industry) => {
      return categoryIndustryMap[category].includes(industry.industry_key);
    });
  },
  /**
   * Returns a industry from it's industry ID
   */
  getIndustry: (state) => (industryId) => {
    return state.industries.find((industry) => {
      return industry.AC_industry_id === industryId;
    });
  },
  isValidCountry: (state) => {
    return state.userCountry === 'GB' ? true : false;
  }
};

const mutations = {
  setCandidateCount(state, value) {
    state.candidateCount = value;
  },
  setEmployerCount(state, value) {
    state.employerCount = value;
  },
  setPopularLocations(state, popularLocations) {
    state.popularLocations = popularLocations;
  },
  setIndustries(state, industries) {
    state.industries = industries;
  },
  setIndustryCounts(state, industryCounts) {
    state.industryCounts = industryCounts;
  },
  setLoaded(state, loaded = true) {
    state.loaded = loaded;
  },
  setUserCountry(state, country) {
    state.userCountry = country;
  }
};

const actions = {
  async loadInfo({ state, commit, dispatch }) {
    if (state.loaded) {
      return;
    }
    const response = await axios.get('/api/indexInfo');

    commit('setCandidateCount', response.data.info.candidateCount);
    commit('setEmployerCount', response.data.info.employerCount);
    commit('setPopularLocations', response.data.info.popularLocations);
    commit('setIndustries', response.data.info.industries);
    commit('setIndustryCounts', response.data.info.industryCounts);
    dispatch('pagetext/updateState', response.data.pagetext, { root: true });
    commit('setLoaded');
  },
};

export default {
  namespaced: true,
  actions,
  mutations,
  getters,
  state: defaultState,
};
